import React, { useState, useEffect, useMemo } from 'react';
import Loader from 'cccisd-loader';
import { useParams, Link } from 'cccisd-react-router';
import IconQuill from 'cccisd-icons/quill3';
import iconArrow from './arrow.svg';
import useStudentPicker from 'js/hooks/useStudentPicker.js';
import useLocalStorage from 'js/hooks/useLocalStorage.js';
import useStudentAssessments from './useStudentAssessments.js';
import AssessmentFilter from './AssessmentFilter';
import SummaryReport from './SummaryReport';
import getNavs from './getNavs.js';
import style from './style.module.scss';
import IconInfo from 'cccisd-icons/info2';

const Results = props => {
    const { pawnId } = useParams();
    const [initialPawnId, setInitialPawnId] = useLocalStorage('resultsStudentId');
    const {
        loading: studentsLoading,
        total: totalStudents,
        selected: selectedStudent,
        renderField,
    } = useStudentPicker({
        initialValue: Number(pawnId) || Number(initialPawnId),
    });

    const { loading: assessmentsLoading, data: assessments } = useStudentAssessments(selectedStudent?.pawnId);
    const [selectedAssessments, setSelectedAssessments] = useState(assessments);

    useEffect(() => {
        if (selectedStudent && initialPawnId !== selectedStudent.pawnId) {
            setInitialPawnId(selectedStudent.pawnId);
        }
    }, [selectedStudent]);

    useEffect(() => {
        setSelectedAssessments(assessments);
    }, [assessments]);

    const navs = useMemo(() => {
        if (!selectedAssessments || !selectedStudent) {
            return [];
        }

        const surveyIds = [
            ...selectedAssessments.reduce((set, item) => {
                for (const assessment of item.assessments) {
                    for (const surveyId of assessment.surveyIds) {
                        set.add(surveyId);
                    }
                }
                return set;
            }, new Set()),
        ];

        const reporterPawnIds = [
            ...selectedAssessments.reduce((set, item) => {
                for (const reporterPawnId of Object.keys(item.pawnDeploymentIds)) {
                    set.add(Number(reporterPawnId));
                }
                return set;
            }, new Set()),
        ];

        return getNavs(surveyIds, reporterPawnIds, selectedStudent);
    }, [selectedAssessments, selectedStudent]);

    const deploymentIds = useMemo(() => {
        if (!selectedAssessments) {
            return [];
        }

        return selectedAssessments.reduce((arr, item) => {
            arr.push(...item.deploymentIds);
            return arr;
        }, []);
    }, [selectedAssessments]);

    const pawnDeploymentIds = useMemo(() => {
        if (!selectedAssessments) {
            return {};
        }

        return selectedAssessments.reduce((obj, item) => {
            for (const key of Object.keys(item.pawnDeploymentIds)) {
                obj[key] ||= [];
                obj[key].push(...item.pawnDeploymentIds[key]);
            }
            return obj;
        }, {});
    }, [selectedAssessments]);

    if (studentsLoading || assessmentsLoading) {
        return <Loader loading />;
    }

    if (totalStudents === 0) {
        return (
            <div className="alert alert-primary text-center fs-3" role="alert">
                <IconInfo /> Add Students on the{' '}
                <Link
                    to={{
                        pathname: '/students',
                    }}
                    className="alert-link"
                >
                    Students
                </Link>{' '}
                page and administer assessments to see results.
            </div>
        );
    }

    return (
        <div>
            <div className={style.studentArea} data-student-area>
                <div>
                    {renderField()}
                    {selectedStudent && (
                        <div className="mt-2" style={{ fontSize: '0.8rem' }}>
                            <span>
                                <b>School:</b> {selectedStudent.school}
                            </span>
                            <span className="ms-4">
                                <b>Student ID:</b> {selectedStudent.studentId}
                            </span>
                        </div>
                    )}
                </div>
                {selectedStudent && (
                    <AssessmentFilter
                        assessments={assessments}
                        initialValues={selectedAssessments}
                        onSubmit={setSelectedAssessments}
                    />
                )}
            </div>
            {(() => {
                if (!selectedStudent) {
                    return (
                        <div className={style.selectStudentInvitation}>
                            <img src={iconArrow} alt="" />
                            Choose a student to see assessment results.
                        </div>
                    );
                }

                if (selectedStudent.totalAssignments === 0) {
                    return (
                        <div className="alert alert-info fs-5 text-center mt-3">
                            <IconQuill spaceRight />
                            The student has no completed assessments.
                        </div>
                    );
                }

                if (!assessments || !selectedAssessments) {
                    return null;
                }

                if (assessments.length === 0) {
                    return (
                        <div className="alert alert-info fs-5 text-center mt-3">
                            <IconQuill spaceRight />
                            The student has no completed assessments.
                        </div>
                    );
                }

                if (selectedAssessments.length === 0) {
                    return (
                        <div className="alert alert-info fs-5 text-center mt-3">
                            <IconQuill spaceRight />
                            No assessment selected.
                        </div>
                    );
                }

                if (navs.length === 0) {
                    return (
                        <div className="alert alert-info fs-5 text-center mt-3">
                            <IconQuill spaceRight />
                            No report is available for selected assessments.
                        </div>
                    );
                }

                const key = navs
                    .reduce(
                        (arr, nav) => {
                            arr.push(nav.id);
                            arr.push(...nav.measures.map(item => item.id));
                            return arr;
                        },
                        [selectedStudent.pawnId]
                    )
                    .join('-');

                return (
                    <SummaryReport
                        key={key}
                        navs={navs}
                        deploymentIds={deploymentIds}
                        pawnDeploymentIds={pawnDeploymentIds}
                        student={selectedStudent}
                    />
                );
            })()}
        </div>
    );
};

export default Results;
